import { useEffect, useState } from "react";
import moment from "moment";
import 'moment/locale/pt-br';
import Grid from "@mui/material/Grid";
import { Briefcase, HandshakeIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Templete from "../../template/Template";
import SimpleBackdrop from "../../components/Loading/SimpleBackdrop";
import { AlertInfo } from "../../components/Alerts/Alert";
import { api } from "../../services/api";
import { TarefaType } from "../../types/TarefaType";
import TarefasPendentesDialog from "../Tarefas/TarefasPendentesDialog";

import "./administrativo.css";

const COLORS = ['#1a237e', '#0277bd', '#00b0ff', '#00c853', '#ffd600'];

const monthlyDataCreate = () => {
    moment.locale('pt-br');
    let months = [];
    for (let index = 0; index < 10; index++) {
        const m = moment().subtract(index, 'months').format('MMM');
        const mn = m.charAt(0).toUpperCase() + m.substring(1);
        const el = { month: mn, cases: 0, index };
        months.push(el);
    }
    return months.reverse();
}

export default function Administrativo() {
    const [openLoad, setOpenLoad] = useState(false);
    const [count, setCount] = useState({
        total: 0,
        emPreparacao: 0,
        emAndamento: 0,
        doMes: 0,
        novosClientes: 0,
    });
    const [monthlyData, setMonthyData] = useState(monthlyDataCreate());
    const [caseTypeData, setCaseTypeData] = useState([
        { servico_advogado: 'CÍVEL', value: 0 },
        { servico_advogado: 'ASSISTENCIAL', value: 0 },
        { servico_advogado: 'PREVIDENCIÁRIO', value: 0 }
    ]);
    const [tarefasPendentes, setTarefasPendentes] = useState<TarefaType[]>([]);
    const [openTarefasDialog, setOpenTarefasDialog] = useState(false);

    function completMonths(months: any[]) {
        const complet = monthlyData.map(i => {
            const is = months.filter(r => r.index === i.index);
            if (is.length === 1) return { ...i, cases: is[0].cases };
            return i;
        });
        setMonthyData(complet);
    }

    async function loadData() {
        setOpenLoad(true);
        try {
            const response = await api.get(`/home`);
            if (response && response.status === 200 && response.data) {
                if (response.data.success) {
                    const { processActives, processActivesAll, inPrepare, inAndamente, newClients, months, processByArea } = response.data.data;
                    setCount({
                        total: processActivesAll ? processActivesAll.value : 0,
                        emPreparacao: inPrepare ? inPrepare.value : 0,
                        emAndamento: inAndamente ? inAndamente.value : 0,
                        doMes: processActives ? processActives.value : 0,
                        novosClientes: newClients ? newClients.value : 0,
                    });
                    if (months) completMonths(months);
                    if (processByArea) setCaseTypeData(processByArea);
                }
            }
        } catch (error) {
            console.log(error);
            AlertInfo({ icon: 'error', title: 'Falha na conexão' });
        }
        setOpenLoad(false);
    }

    async function loadTarefasPendentes() {
        const jaExibido = sessionStorage.getItem('tarefas_popup_exibido');
        if (jaExibido) return;

        try {
            const response = await api.get(`/tarefas/pendentes`);
            if (response.data && !response.data.error && response.data.length > 0) {
                setTarefasPendentes(response.data);
                setOpenTarefasDialog(true);
                sessionStorage.setItem('tarefas_popup_exibido', '1');
            }
        } catch (error) {
            console.log(error);
        }
    }

    function handleTarefasUpdated() {
        setOpenTarefasDialog(false);
        loadTarefasPendentes();
    }

    useEffect(() => {
        loadData();
        loadTarefasPendentes();
    }, []);

    return (
        <Templete menuActive="administrativo">
            <SimpleBackdrop open={openLoad} />

            <TarefasPendentesDialog
                open={openTarefasDialog}
                tarefas={tarefasPendentes}
                onClose={() => setOpenTarefasDialog(false)}
                onUpdated={handleTarefasUpdated}
            />
            <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    <div className="stat-card">
                        <h3>Processos Ativos</h3>
                        <div className="value">{count.total}</div>
                    </div>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <div className="stat-card">
                        <h3>Processos em preparação</h3>
                        <div className="value">{count.emPreparacao}</div>
                    </div>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <div className="stat-card">
                        <h3>Andamento de Processos</h3>
                        <div className="value">{count.emAndamento}</div>
                    </div>
                </Grid>
                <Grid item xs={false} sm={2}></Grid>
                <Grid item xs={12} sm={4}>
                    <div className="stat-card">
                        <h3>Processos do Mês</h3>
                        <div className="value">{count.doMes}</div>
                    </div>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <div className="stat-card">
                        <h3>Novos Clientes</h3>
                        <div className="value">{count.novosClientes}</div>
                    </div>
                </Grid>
                <Grid item xs={false} sm={2}></Grid>
                <Grid item xs={12} sm={6}>
                    <div className="charts-grid">
                        <div className="chart-card">
                            <h3>
                                <Briefcase size={24} />
                                Historico dos ultimos 10 meses
                            </h3>
                            <div className="chart-container">
                                <ResponsiveContainer width="100%" height={350}>
                                    <BarChart data={monthlyData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="month" />
                                        <YAxis yAxisId="left" />
                                        <YAxis yAxisId="right" orientation="right" />
                                        <Tooltip />
                                        <Legend />
                                        <Bar yAxisId="left" dataKey="cases" fill="#1a237e" name="Processos" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                    </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <div className="chart-card">
                        <h3>
                            <HandshakeIcon size={24} />
                            Percentual de Areas
                        </h3>
                        <div className="chart-container">
                            <ResponsiveContainer width="100%" height={350}>
                                <PieChart>
                                    <Pie
                                        data={caseTypeData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ servico_advogado, percent }) => `${servico_advogado} ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={130}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {caseTypeData.map((_, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => [`Processos: ${value}`]} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </Grid>

            </Grid>
        </Templete>
    );
}
